import React, { useState, useRef } from 'react';
import { FileText, Printer, X, Download } from 'lucide-react';
import api from '../../api/client';
import { downloadPdf } from '../../utils/pdfUtils';
import PdfCanvasViewer, { precisaPdfCanvas } from '../common/PdfCanvasViewer';

const CatalogoLeilaoButton = ({ leilaoId, organizadorId, label = 'Catálogo Leilão', endpoint = 'catalogo-leilao', leilaoNome = '', permitirPaisagem = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [pdfUrl, setPdfUrl]       = useState(null);
  const [loading, setLoading]     = useState(false);
  // Orientação escolhida no momento de imprimir — não é salva em config,
  // vale só pra essa geração/impressão (ver backend/catalogos/routes.py,
  // query param `orientacao`).
  const [orientacao, setOrientacao] = useState('retrato');
  const iframeRef = useRef();

  const loadPdf = async (orientacaoEscolhida = orientacao) => {
    try {
      setLoading(true);
      const response = await api.get(
        `/organizadores/${organizadorId}/catalogos/leiloes/${leilaoId}/${endpoint}`,
        {
          responseType: 'blob', timeout: 60000,
          params: permitirPaisagem ? { orientacao: orientacaoEscolhida } : undefined,
        }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      setPdfUrl(URL.createObjectURL(blob));
    } catch (error) {
      if (error.response?.status === 401) return;
      console.error('Erro ao gerar catálogo:', error);
      alert('Erro ao gerar catálogo. Verifique se o backend está rodando.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => { setShowModal(true); loadPdf(orientacao); };

  const handleTrocarOrientacao = (nova) => {
    if (nova === orientacao) return;
    setOrientacao(nova);
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
    loadPdf(nova);
  };

  const handleDownload = async () => {
    if (!pdfUrl) return;
    const nomeBase = endpoint === 'catalogo-pisteiro' ? 'Catálogo Pisteiro'
      : endpoint === 'catalogo-detalhado' ? 'Catálogo Detalhado'
      : endpoint === 'ficha-leiloeiro' ? 'Ficha do Leiloeiro'
      : 'Catálogo Leilão';
    const filename = `${nomeBase} - ${leilaoNome || leilaoId}.pdf`.replace(/[/\\?%*:|"<>]/g, '-');
    await downloadPdf(pdfUrl, filename);
  };

  const handlePrint = () => {
    if (precisaPdfCanvas()) {
      // Sem iframe no Android: abre o PDF numa aba, onde o Chrome oferece imprimir/compartilhar
      window.open(pdfUrl, '_blank');
    } else if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus();
      iframeRef.current.contentWindow.print();
    }
  };

  const handleClose = () => {
    setShowModal(false);
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
  };

  return (
    <>
      <button
        onClick={handleOpen}
        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm font-medium"
        title={label}
      >
        <FileText size={16} />
        {label}
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white shadow-2xl w-full sm:max-w-6xl sm:rounded-xl h-full sm:h-[92vh] flex flex-col">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 border-b border-gray-200 bg-gray-50 sm:rounded-t-xl flex-shrink-0">
              <div className="flex items-center justify-between sm:justify-start gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-indigo-100 rounded-lg flex-shrink-0">
                    <FileText size={24} className="text-indigo-600" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base sm:text-xl font-semibold text-gray-900 truncate">{label}</h2>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">{leilaoNome || `Leilão #${leilaoId}`}</p>
                  </div>
                </div>
                <button onClick={handleClose} className="sm:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0">
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              {permitirPaisagem && (
                <div className="flex sm:hidden items-center rounded-lg border border-gray-300 overflow-hidden text-sm">
                  <button
                    onClick={() => handleTrocarOrientacao('retrato')}
                    disabled={loading}
                    className={`flex-1 px-3 py-2 transition-colors ${orientacao === 'retrato' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600'}`}
                  >
                    Retrato
                  </button>
                  <button
                    onClick={() => handleTrocarOrientacao('paisagem')}
                    disabled={loading}
                    className={`flex-1 px-3 py-2 transition-colors border-l border-gray-300 ${orientacao === 'paisagem' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600'}`}
                  >
                    Paisagem
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2">
                {permitirPaisagem && (
                  <div className="hidden sm:flex items-center rounded-lg border border-gray-300 overflow-hidden text-sm">
                    <button
                      onClick={() => handleTrocarOrientacao('retrato')}
                      disabled={loading}
                      className={`px-3 py-2 transition-colors ${orientacao === 'retrato' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      Retrato
                    </button>
                    <button
                      onClick={() => handleTrocarOrientacao('paisagem')}
                      disabled={loading}
                      className={`px-3 py-2 transition-colors border-l border-gray-300 ${orientacao === 'paisagem' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      Paisagem
                    </button>
                  </div>
                )}
                <button onClick={handleDownload} disabled={!pdfUrl}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors text-sm">
                  <Download size={16} /> <span className="hidden sm:inline">Baixar PDF</span>
                </button>
                <button onClick={handlePrint} disabled={!pdfUrl}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition-colors text-sm">
                  <Printer size={16} /> Imprimir
                </button>
                <button onClick={handleClose} className="hidden sm:block p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Gerando catálogo...</p>
                  </div>
                </div>
              ) : pdfUrl ? (
                precisaPdfCanvas() ? (
                  <PdfCanvasViewer url={pdfUrl} />
                ) : (
                  <iframe ref={iframeRef} src={pdfUrl} title={label}
                    style={{ width: '100%', height: '100%', border: 'none' }} />
                )
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">PDF não disponível</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CatalogoLeilaoButton;
